import React, { Suspense } from 'react';
import { styled } from '@mui/material/styles';
import { Button, Typography, Divider } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid2';

import AdaptiveProjectChart from './AdaptiveProjectChart';
import PurchaseOrderStatusPie from './PurchaseOrderStatusPie';
import SpendByVendorTreemap from './SpendByVendorTreemap';

import GanttDisplay from './Lazy/LazyGanttDisplay';
import NotesGrid from './Lazy/LazyNotesGrid';
import PurchaseOrdersDisplay from './Lazy/LazyPurchaseOrdersDisplay';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: '#fff',
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  ...theme.applyStyles('dark', {
    backgroundColor: '#1A2027',
  }),
}));

export default function AdaptiveCost({ onClose, rowData, refetch }) {
  return (
    <Box sx={{ width: '100%', p: 3 }}>
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'white',
          borderBottom: '1px solid #ddd',
          p: 2,
        }}
      >
        {/* Back Button */}
        <Button variant="contained" onClick={onClose} startIcon={<ArrowBackIcon />}>
          Back
        </Button>

        {/* Title */}
        <Typography variant="h5" sx={{ flexGrow: 1, textAlign: 'center', fontWeight: 'bold' }}>
          Project Dashboard: {rowData?.AFEName}
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 7 }}>
            <Item
              sx={{
                p: 3,
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                maxHeight: '60vh',
                overflowY: 'auto',
                boxShadow: 4,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Notes Input Area
              </Typography>
              <Suspense fallback={<Typography>Loading Notes...</Typography>}>
                <NotesGrid
                  notes={rowData.activities}
                  projectId={rowData?.projectId}
                  projectData={rowData}
                  refetch={refetch}
                />
              </Suspense>
            </Item>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <Item
              sx={{
                p: 3,
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
                boxShadow: 4,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Adaptive Project Summary Capex Models
              </Typography>

              <Box sx={{ flex: 1, width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' }}>
                <AdaptiveProjectChart data={rowData || []} />
              </Box>
            </Item>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Item
              sx={{
                p: 3,
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                maxHeight: '60vh',
                overflowY: 'auto',
                boxShadow: 4,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                Purchase Order Display
              </Typography>
              <PurchaseOrdersDisplay purchaseOrders={rowData?.purchaseOrders || []} />
            </Item>
          </Grid>

          <Grid size={{ xs: 12, md: 5 }}>
            <Item
              sx={{
                p: 3,
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
                boxShadow: 4,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                NetSuite Purchase Order Status
              </Typography>
              <PurchaseOrderStatusPie data={rowData.purchaseOrders || []} />
            </Item>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Item
              sx={{
                p: 3,
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                overflow: 'hidden',
                boxShadow: 4,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                NetSuite AFE Spend by Vendor
              </Typography>
              <SpendByVendorTreemap purchaseOrders={rowData?.purchaseOrders || []} />
            </Item>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Item
              sx={{
                p: 3,
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                maxHeight: '60vh',
                overflowY: 'auto',
                boxShadow: 4,
              }}
            >
              <GanttDisplay purchaseOrders={rowData?.purchaseOrders || []} />
            </Item>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
